import {useRef, useState} from 'react'
import {clsx} from 'clsx'
import {LinkButton} from '@primer/react'
import {CommandPaletteIcon} from '@primer/octicons-react'
import type {GettingStarted, ShowModelPayload} from '../../../types'
import type {Model} from '@github-ui/marketplace-common'
import {modelPlaygroundPath} from '@github-ui/paths'
import {useRoutePayload} from '@github-ui/react-core/use-route-payload'
import {ModelDetails} from '../../../components/ModelDetailsSidebar/ModelDetails'
import GettingStartedDialog from '../../playground/components/GettingStartedDialog/GettingStartedDialog'
import {BreadcrumbHeader} from './BreadcrumbHeader'
import {GiveFeedback} from '../../../components/GiveFeedback'
import {PublisherAvatar} from '../../../components/PublisherAvatar'
import {UseThisModelButton} from '../../../components/UseThisModelButton'
import {userHasAccessToModel} from '../../../utils/model-access'
import styles from './ModelOverviewHeader.module.css'
import responsiveStyles from '../../../components/responsive.module.css'

export function ModelOverviewHeader({
  model,
  gettingStarted,
  canUseModel,
}: {
  model: Model
  gettingStarted: GettingStarted
  canUseModel: boolean
}) {
  const {summary} = model
  const [isGettingStartedDialogOpen, setGettingStartedDialogOpen] = useState(false)
  const {restrictedModels, isLoggedIn} = useRoutePayload<ShowModelPayload>()
  const useThisModelRef = useRef(null)

  const showAPIKey = userHasAccessToModel(model, restrictedModels)

  return (
    <div className={clsx('d-flex flex-column gap-3', styles.narrowPadding)}>
      {/*
        the breadcrumb header is redundant with the global navigation,
        so only include it when users are logged out (as logged out users do not see the global nav)
      */}
      {!isLoggedIn && <BreadcrumbHeader model={model} />}
      <div className="d-flex gap-3 flex-column flex-md-row">
        {/* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */}
        <div className="d-flex flex-auto gap-2 align-items-left">
          <PublisherAvatar
            logoUrl={model.logo_url}
            darkModeIcon={model.dark_mode_icon}
            publisher={model.publisher}
            size={{narrow: 24, regular: 32}}
          />
          <h1 className={clsx('h3', styles.narrowHeading)}>{model.friendly_name}</h1>
        </div>

        <span className={responsiveStyles.hideOnRegular}>{summary}</span>
        <div className={responsiveStyles.hideOnRegular}>
          <ModelDetails model={model} direction="row" />
        </div>

        <div className={clsx('d-flex gap-2', styles.narrowWidthFull)}>
          <GiveFeedback className={responsiveStyles.hideOnNarrow} />
          <div className="flex-1 d-flex gap-2 flex-column flex-md-row">
            {canUseModel && (
              <LinkButton
                variant="default"
                className={styles.narrowBlock}
                href={modelPlaygroundPath(model)}
                leadingVisual={CommandPaletteIcon}
                tabIndex={0}
              >
                Playground
              </LinkButton>
            )}
            {showAPIKey && (
              <UseThisModelButton
                tabIndex={0}
                className={styles.narrowBlock}
                ref={useThisModelRef}
                variant="primary"
                model={model}
                hideLabelOnSmallScreens={false}
                onClick={() => setGettingStartedDialogOpen(true)}
              />
            )}
          </div>
          {isGettingStartedDialogOpen && (
            <GettingStartedDialog
              onClose={() => setGettingStartedDialogOpen(false)}
              gettingStarted={gettingStarted}
              modelName={model.name}
              returnFocusRef={useThisModelRef}
            />
          )}
        </div>
      </div>
    </div>
  )
}
